/**
 * Flat operational_category taxonomy for the classification review dropdown.
 *
 * Source of truth: the `operational_categories` keys declared in the F3
 * rulesets at services/classifier/app/rulesets/invoice/ruleset_36_*.json.
 * Keep this list in sync when a ruleset adds or renames a key.
 *
 * F3 eliminated the two-level (category → subcategory) hierarchy. The
 * classifier now emits a single operational_category per document, and the
 * eligibility resolver downstream maps it to fiscal rules.
 */

import type { FilterOption, OptionGroup } from "@/domain/types";

import { TaxIncentiveType } from "./constants";

// ── Types ───────────────────────────────────────────────────────────

interface OperationalCategory {
  id: string;
  name: string;
}

// ── Data — Art. 36.3 (música / artes en vivo) ──────────────────────

const MUSIC_CATEGORIES: OperationalCategory[] = [
  { id: "honorarios_artisticos", name: "Honorarios artísticos" },
  { id: "alojamiento", name: "Alojamiento" },
  { id: "transporte", name: "Transporte" },
  { id: "vehiculos_produccion", name: "Vehículos de producción" },
  { id: "combustible", name: "Combustible" },
  { id: "sonido", name: "Sonido" },
  { id: "iluminacion", name: "Iluminación" },
  { id: "escenarios", name: "Escenarios" },
  { id: "rigging", name: "Rigging" },
  { id: "maquinaria_generadores", name: "Maquinaria y generadores" },
  { id: "decoracion_escenografia", name: "Decoración / escenografía" },
  { id: "material_produccion", name: "Material de producción" },
  { id: "catering", name: "Catering (artistas / crew)" },
  { id: "restauracion", name: "Restauración" },
  { id: "bebidas", name: "Bebidas" },
  { id: "formacion", name: "Formación / educación artística" },
  { id: "hosteleria", name: "Hostelería" },
  { id: "merchandising", name: "Merchandising" },
  { id: "publicidad", name: "Publicidad / agencias" },
  { id: "redes_sociales", name: "Redes sociales" },
  { id: "web_digital", name: "Web y digital" },
  { id: "rrpp", name: "Relaciones públicas" },
  { id: "foto_video", name: "Foto / vídeo" },
  { id: "impresion_grafica", name: "Impresión gráfica" },
  { id: "alquiler_local", name: "Alquiler de local / recinto" },
  { id: "ambientacion_local", name: "Ambientación del local" },
  { id: "limpieza", name: "Limpieza" },
  { id: "seguridad", name: "Seguridad" },
  { id: "asistencia_medica", name: "Asistencia médica" },
  { id: "prevencion_incendios", name: "Prevención de incendios" },
  { id: "infraestructura_recinto", name: "Infraestructura del recinto" },
  { id: "personal_montaje", name: "Personal de montaje" },
  { id: "direccion_produccion", name: "Dirección de producción" },
  { id: "gestoria", name: "Gestoría / administración" },
  { id: "software", name: "Software / herramientas" },
  { id: "telecomunicaciones", name: "Telecomunicaciones" },
  { id: "alquiler_oficina", name: "Alquiler de oficina" },
  { id: "suministros", name: "Suministros" },
  { id: "parking", name: "Parking" },
  { id: "mensajeria", name: "Mensajería / paquetería" },
  { id: "personal_temporal", name: "Personal temporal / ETT" },
];

// ── Data — Art. 36.1 (cine / audiovisual) ──────────────────────────

const CINEMA_CATEGORIES: OperationalCategory[] = [
  { id: "direccion", name: "Dirección" },
  { id: "guion", name: "Guion" },
  { id: "produccion_creativa", name: "Producción creativa" },
  { id: "direccion_fotografia", name: "Dirección de fotografía" },
  { id: "personal_tecnico_camara", name: "Personal técnico de cámara" },
  { id: "personal_tecnico_sonido", name: "Personal técnico de sonido" },
  {
    id: "personal_tecnico_iluminacion",
    name: "Personal técnico de iluminación",
  },
  { id: "grip", name: "Grip / maquinistas" },
  { id: "vestuario", name: "Vestuario" },
  { id: "maquillaje", name: "Maquillaje" },
  { id: "peluqueria", name: "Peluquería" },
  { id: "direccion_arte", name: "Dirección de arte" },
  { id: "decoracion_escenografia", name: "Decoración / escenografía" },
  { id: "atrezzo", name: "Atrezzo / props" },
  { id: "camaras_opticas", name: "Cámaras y ópticas" },
  { id: "iluminacion_cine", name: "Equipo de iluminación cine" },
  { id: "sonido_directo", name: "Sonido directo" },
  { id: "localizaciones", name: "Localizaciones" },
  { id: "permisos_rodaje", name: "Permisos de rodaje" },
  { id: "transporte_rodaje", name: "Transporte de rodaje" },
  { id: "alojamiento_rodaje", name: "Alojamiento de rodaje" },
  { id: "dietas_rodaje", name: "Dietas de rodaje" },
  { id: "vehiculos_rodaje", name: "Vehículos de rodaje" },
  { id: "material_produccion_cine", name: "Material de producción" },
  { id: "seguros_rodaje", name: "Seguros de rodaje" },
  { id: "montaje", name: "Montaje" },
  { id: "posproduccion_sonido", name: "Posproducción de sonido" },
  { id: "posproduccion_color", name: "Posproducción de color" },
  { id: "vfx", name: "VFX" },
  { id: "musica_sincronizacion", name: "Música y sincronización" },
  { id: "marketing_estreno", name: "Marketing de estreno" },
  { id: "trailer", name: "Tráiler" },
  { id: "material_promocional_cine", name: "Material promocional" },
  { id: "prensa_estreno", name: "Prensa y estreno" },
  { id: "gestoria", name: "Gestoría" },
  { id: "software", name: "Software" },
  { id: "alquiler_oficina_productora", name: "Alquiler oficina productora" },
  { id: "telecomunicaciones", name: "Telecomunicaciones" },
];

// ── Lookup map ─────────────────────────────────────────────────────

const categoryMap = new Map<string, string>();
for (const cat of MUSIC_CATEGORIES) categoryMap.set(cat.id, cat.name);
for (const cat of CINEMA_CATEGORIES) categoryMap.set(cat.id, cat.name);
categoryMap.set("desconocido", "Desconocido");

// ── Public API ──────────────────────────────────────────────────────

const MUSIC_GROUP: OptionGroup = {
  label: "Artes escénicas y musicales",
  options: MUSIC_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
};

const CINEMA_GROUP: OptionGroup = {
  label: "Producciones audiovisuales",
  options: CINEMA_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
};

const _categoryGroups: OptionGroup[] = [MUSIC_GROUP, CINEMA_GROUP];

export function getCategoryGroups(incentiveType?: string | null): OptionGroup[] {
  if (incentiveType === TaxIncentiveType.PERFORMING_ARTS_MUSIC) return [MUSIC_GROUP];
  if (incentiveType === TaxIncentiveType.FILM_AUDIOVISUAL) return [CINEMA_GROUP];
  return _categoryGroups;
}

export function getCategoryOptions(): FilterOption[] {
  return [...MUSIC_CATEGORIES, ...CINEMA_CATEGORIES].map((c) => ({
    value: c.id,
    label: c.name,
  }));
}

export function getCategoryName(categoryId: string): string | null {
  return categoryMap.get(categoryId) ?? null;
}
